/**
 * Aufgaben-Katalog. Neue Aufgabe = neuer Eintrag in `TASKS` (Format siehe
 * `types.ts`). Die Reihenfolge innerhalb einer Schwierigkeit ist die
 * vorgeschlagene Lernreihenfolge. `tasks.test.ts` prüft jeden Eintrag.
 *
 * Geometrie-Spickzettel:
 *  - Boxengasse: Nord-Boxen zwischen Steg (y = 0) und Dalben (y = −13,5),
 *    Box n = x (n−1)·4,2 … n·4,2; Süd-Boxen zwischen Dalben (y = −37,5) und
 *    Steg (y = −51). Freie Boxen: Nord 3, 7, 10, 12, 16 · Süd 2, 8, 13, 17.
 *  - Längsseits: Steg y = 0, Lücke A x 20…38, Lücke B x 62…76, Poller k bei
 *    x = −4 + 3·(k−1). Gegenüber Boxen (Dalben y = −24,5), frei: Süd 4, 11, 18.
 *  - Enge Gasse: Boxen 4,0 × 12,5 m, Fahrwasser y −27,5 … −12,5 (15 m).
 *    Frei: Nord 4, 9, 14 · Süd 6, 12.
 *  - Seitengassen: Hauptfahrwasser y −22 … 0 mit Südboxen (frei s3, s8, s15,
 *    s22). Gasse 1 (16 m, x ≈ 12) frei: g1-n3, g1-n6 (West), g1-s2 (Ost).
 *    Gasse 2 (12 m, x ≈ 74, Wenden praktisch unmöglich) frei: g2-n2, g2-n5 (West),
 *    g2-s4 (Ost). Boxennummern zählen von der Gassenmündung nach Norden.
 */
import type { TaskDef } from './types';
import { zoneRect } from './types';

/** Hafenausfahrt (Westende der Gasse) als Zielzone beim Ablegen */
const EXIT_BOXENGASSE = zoneRect(-42, -35, -25, -16);
const EXIT_LAENGSSEITS = zoneRect(-42, -22, -25, -6);
const EXIT_ENGE = zoneRect(-37, -26, -24, -14);
const EXIT_SEITEN = zoneRect(-42, -20, -28, -2);

export const TASKS: TaskDef[] = [
  // ------------------------------------------------------------ 1 Einsteiger
  {
    id: 'box-flaute',
    title: 'Erste Box bei Flaute',
    difficulty: 1,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n10', orientation: 'bowToPier' },
    briefing:
      'Kein Wind, keine Strömung: Gefühl für Gas, Ruder und Aufstoppen bekommen. Früh auskuppeln – die Yacht gleitet weit. Heckleinen im Vorbeifahren über die Dalben legen, dann Bugleinen an die Stegklampen.',
    tags: ['Box', 'Grundlagen'],
  },
  {
    id: 'box-brise-vorn',
    title: 'Leichte Brise von vorn',
    difficulty: 1,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n7', orientation: 'bowToPier' },
    env: { windSpeedKn: 6, windFromDeg: 0, gustiness: 0.1, windShiftDeg: 5 },
    briefing: 'Der Wind steht in die Box hinein und bremst mit. Etwas mehr Fahrt halten als bei Flaute, sonst treibt der Bug ab.',
    tags: ['Box', 'Wind'],
  },
  {
    id: 'laengsseits-flaute',
    title: 'Längsseits bei Flaute',
    difficulty: 1,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-a' },
    briefing:
      'Flach (ca. 20–30°) auf die Lücke zufahren, rechtzeitig Fender auf der Stegseite ausbringen. Vorleine, Achterleine und beide Springs belegen und dichtholen.',
    tags: ['Längsseits', 'Grundlagen', 'Fender'],
  },
  // ---------------------------------------------------------------- 2 Leicht
  {
    id: 'box-rueckenwind',
    title: 'Rückenwind in die Box',
    difficulty: 2,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n10', orientation: 'bowToPier' },
    env: { windSpeedKn: 10, windFromDeg: 180, gustiness: 0.2, windShiftDeg: 8 },
    briefing: 'Der Wind schiebt in die Box. Sehr früh auskuppeln, kurz rückwärts aufstoppen – und die Heckleinen an den Dalben als Bremse nutzen (fieren, dann belegen).',
    tags: ['Box', 'Wind', 'Aufstoppen'],
  },
  {
    id: 'laengsseits-auflandig',
    title: 'Auflandiger Wind längsseits',
    difficulty: 2,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-a' },
    env: { windSpeedKn: 10, windFromDeg: 180, gustiness: 0.2, windShiftDeg: 8 },
    briefing: 'Der Wind drückt Richtung Steg. Parallel neben die Lücke setzen und sich vertreiben lassen – vorher Fender raus, sonst gibt es Kontakt.',
    tags: ['Längsseits', 'Wind', 'Fender'],
  },
  {
    id: 'ablegen-box',
    title: 'Ablegen aus der Box',
    difficulty: 2,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n10', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    env: { windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15, windShiftDeg: 8 },
    startMoored: 'bowToPier',
    briefing: 'Bugleinen los, Heckleinen auf Slip halten und beim Rückwärtsfahren fieren. Radeffekt einplanen: rückwärts zieht das Heck nach Backbord. Dann zur Hafenausfahrt im Westen.',
    tags: ['Box', 'Ablegen'],
  },
  {
    id: 'box-eng',
    title: 'Enge Box zwischen Nachbarn',
    difficulty: 2,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n3', orientation: 'bowToPier' },
    env: { windSpeedKn: 6, windFromDeg: 250, gustiness: 0.15, windShiftDeg: 8 },
    briefing: 'Box 3 liegt nah an der Einfahrt – wenig Anlauf. Langsam und gerade zwischen die Dalben, auf beiden Seiten liegen Nachbarn.',
    tags: ['Box', 'Präzision'],
  },
  // ----------------------------------------------------------------- 3 Mittel
  {
    id: 'box-seitenwind',
    title: 'Seitenwind in der Box',
    difficulty: 3,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n10', orientation: 'bowToPier' },
    env: { windSpeedKn: 14, windFromDeg: 270, gustiness: 0.3, windShiftDeg: 10 },
    briefing: 'Wind quer zur Box: Luv-Dalbe zuerst belegen, das Boot hängt sich daran. Mit Fahrt einfahren, sonst fällt der Bug ab.',
    tags: ['Box', 'Wind'],
  },
  {
    id: 'box-rueckwaerts',
    title: 'Rückwärts in die Box',
    difficulty: 3,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n12', orientation: 'sternToPier' },
    env: { windSpeedKn: 5, windFromDeg: 200, gustiness: 0.1, windShiftDeg: 5 },
    briefing: 'Heck zum Steg. Erst mit etwas Fahrt achteraus Steuerwirkung aufbauen, Radeffekt (Heck nach Bb) vorhalten. Bugleinen an die Dalben, Heckleinen an den Steg.',
    tags: ['Box', 'Rückwärts', 'Radeffekt'],
  },
  {
    id: 'laengsseits-ablandig',
    title: 'Ablandiger Wind längsseits',
    difficulty: 3,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-a' },
    env: { windSpeedKn: 12, windFromDeg: 0, gustiness: 0.25, windShiftDeg: 8 },
    briefing: 'Der Wind drückt vom Steg weg. Steiler anfahren, zuerst die Vorspring belegen und in die Spring eindampfen – das Heck kommt an den Steg.',
    tags: ['Längsseits', 'Wind', 'Eindampfen'],
  },
  {
    id: 'laengsseits-stb',
    title: 'Steuerbord längsseits',
    difficulty: 3,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-a', orientation: 'starboardSide' },
    start: { pos: { x: 92, y: -14 }, headingDeg: 270, speedKn: 1.5 },
    env: { windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15, windShiftDeg: 8 },
    briefing: 'Einfahrt von Osten, Steuerbordseite zum Steg. Fender an Steuerbord nicht vergessen – der Radeffekt beim Aufstoppen zieht das Heck vom Steg weg.',
    tags: ['Längsseits', 'Radeffekt'],
  },
  {
    id: 'box-stroemung',
    title: 'Strömung in der Gasse',
    difficulty: 3,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n16', orientation: 'bowToPier' },
    env: { currentSpeedKn: 0.8, currentTowardDeg: 90, windSpeedKn: 5, windFromDeg: 180, gustiness: 0.1 },
    briefing: 'Die Strömung setzt mit 0,8 kn die Gasse entlang nach Osten. Beim Eindrehen stromauf vorhalten, sonst versetzt es dich auf die Nachbarbox.',
    tags: ['Box', 'Strömung'],
  },
  // ------------------------------------------------------------------ 4 Schwer
  {
    id: 'ablegen-vorspring',
    title: 'Ablegen: Eindampfen in die Vorspring',
    difficulty: 4,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-a', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    env: { windSpeedKn: 14, windFromDeg: 180, gustiness: 0.3, windShiftDeg: 8 },
    startMoored: 'portSide',
    briefing:
      'Auflandiger Wind drückt dich an den Steg. Alle Leinen außer der Vorspring los, Ruder zum Steg, langsam voraus eindampfen: das Heck schwenkt ab. Dann rückwärts frei und zur Ausfahrt. Ballfender am Bug hilft.',
    tags: ['Längsseits', 'Ablegen', 'Eindampfen'],
  },
  {
    id: 'luecke-b',
    title: 'Lücke B – 14 Meter',
    difficulty: 4,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-b' },
    env: { windSpeedKn: 8, windFromDeg: 220, gustiness: 0.2, windShiftDeg: 8 },
    briefing: 'Nur 1,5 Bootslängen Platz. Mit Achterspring eindampfen oder rückwärts einparken – Ballfender an die neuralgische Ecke.',
    tags: ['Längsseits', 'Präzision'],
  },
  {
    id: 'box-sued-rueckwaerts',
    title: 'Rückwärts gegen den Radeffekt',
    difficulty: 4,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-s8', orientation: 'sternToPier' },
    env: { windSpeedKn: 10, windFromDeg: 330, gustiness: 0.25, windShiftDeg: 10 },
    briefing: 'Rückwärts in eine Süd-Box: hier arbeitet der Radeffekt gegen dich. Mit kurzen Gasstößen und Fahrt achteraus steuern, Wind von achtern-quer beachten.',
    tags: ['Box', 'Rückwärts', 'Radeffekt'],
  },
  {
    id: 'box-starkwind',
    title: 'Starkwind querab in die Box',
    difficulty: 4,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n10', orientation: 'bowToPier' },
    env: { windSpeedKn: 20, windFromDeg: 270, gustiness: 0.4, windShiftDeg: 12 },
    briefing: '20 kn mit Böen quer zur Box. Zügig einfahren, Luv-Dalbe sofort belegen, Maschine bleibt drin bis alle Leinen fest sind.',
    tags: ['Box', 'Wind'],
  },
  {
    id: 'langkieler-box',
    title: 'Langkieler in die Box',
    difficulty: 4,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-n7', orientation: 'bowToPier' },
    env: { windSpeedKn: 10, windFromDeg: 240, gustiness: 0.2, windShiftDeg: 8 },
    yacht: 'sy36-long',
    briefing: 'Klassischer Langkieler: träge, großer Drehkreis, kräftiger Radeffekt. Früh eindrehen, Schraubenstrahl aufs Ruder nutzen.',
    tags: ['Box', 'Langkiel'],
  },
  {
    id: 'ablegen-seitenwind',
    title: 'Ablegen bei Seitenwind',
    difficulty: 4,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n10', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    env: { windSpeedKn: 16, windFromDeg: 270, gustiness: 0.3, windShiftDeg: 10 },
    startMoored: 'bowToPier',
    briefing: 'Seitenwind drückt dich beim Rausfahren auf die Lee-Dalbe. Luv-Heckleine zuletzt los und damit das Heck führen, zügig rückwärts raus.',
    tags: ['Box', 'Ablegen', 'Wind'],
  },
  {
    id: 'laengsseits-strom-achtern',
    title: 'Strom von achtern längsseits',
    difficulty: 4,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-a' },
    env: { currentSpeedKn: 1, currentTowardDeg: 90, windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15 },
    briefing: 'Strömung von achtern schiebt dich mit 1 kn. Lieber gegen den Strom anlegen (wenden!) – oder sehr früh aufstoppen und Achterspring zuerst.',
    tags: ['Längsseits', 'Strömung'],
  },
  // ----------------------------------------------------------------- 5 Experte
  {
    id: 'box-sturm-rueckwaerts',
    title: 'Sturmböen – rückwärts in Box Süd',
    difficulty: 5,
    harbor: 'boxengasse',
    goal: { kind: 'moor', berth: 'box-s13', orientation: 'sternToPier' },
    env: { windSpeedKn: 25, windFromDeg: 240, gustiness: 0.5, windShiftDeg: 12, currentSpeedKn: 0.5, currentTowardDeg: 60 },
    briefing: '25 kn, kräftige Böen, dazu Strömung. Rückwärts mit Schwung, Böen abwarten, Luv-Leinen zuerst. Fender auf beiden Seiten.',
    tags: ['Box', 'Rückwärts', 'Starkwind'],
  },
  {
    id: 'luecke-b-ablandig',
    title: 'Lücke B ablandig mit Böen',
    difficulty: 5,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-b' },
    env: { windSpeedKn: 18, windFromDeg: 10, gustiness: 0.45, windShiftDeg: 12 },
    briefing: '14 m Lücke, 18 kn vom Steg weg. Vorspring als erste Leine, dann eindampfen und die übrigen Leinen in Ruhe ausbringen.',
    tags: ['Längsseits', 'Starkwind', 'Eindampfen'],
  },
  {
    id: 'langkieler-luecke-b',
    title: 'Langkieler in Lücke B mit Strömung',
    difficulty: 5,
    harbor: 'laengsseits',
    goal: { kind: 'moor', berth: 'gap-b' },
    env: { currentSpeedKn: 0.8, currentTowardDeg: 270, windSpeedKn: 12, windFromDeg: 200, gustiness: 0.3, windShiftDeg: 10 },
    yacht: 'sy36-long',
    briefing: 'Träger Langkieler, 14 m Lücke, Strömung gegenan und auflandige Böen. Gegen den Strom anlegen, Radeffekt beim Aufstoppen einplanen.',
    tags: ['Längsseits', 'Langkiel', 'Strömung'],
  },
  // ================================================================ Ablegen
  // Boot startet festgemacht (startMoored); Lage und Leinen berechnet die
  // Simulation passend zur Yacht.
  {
    id: 'ablegen-heck-flaute',
    title: 'Vorwärts aus der Box (Heck am Steg)',
    difficulty: 1,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n12', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'sternToPier',
    briefing: 'Du liegst mit dem Heck zum Steg – einfach vorwärts raus. Heckleinen los, Bugleinen an den Dalben zuletzt, dann langsam voraus und in der Gasse nach Westen drehen.',
    tags: ['Box', 'Ablegen', 'Grundlagen'],
  },
  {
    id: 'ablegen-box-flaute',
    title: 'Rückwärts aus der Box bei Flaute',
    difficulty: 1,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n10', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    briefing: 'Bugleinen los, dann rückwärts. Die Heckleinen an den Dalben erst loswerfen, wenn das Heck frei zwischen den Dalben ist. Radeffekt: das Heck zieht nach Backbord.',
    tags: ['Box', 'Ablegen', 'Radeffekt'],
  },
  {
    id: 'ablegen-laengsseits-flaute',
    title: 'Ablegen längsseits bei Flaute',
    difficulty: 1,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-a', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    briefing: 'Ohne Wind: Springs und Achterleine los, Vorleine zuletzt. Bug mit wenig Ruder vom Steg wegführen, das Heck nicht an den Steg drücken.',
    tags: ['Längsseits', 'Ablegen', 'Grundlagen'],
  },
  {
    id: 'ablegen-box-gegenwind',
    title: 'Ablegen mit Wind von vorn',
    difficulty: 2,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n7', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 8, windFromDeg: 0, gustiness: 0.2, windShiftDeg: 8 },
    briefing: 'Der Wind kommt vom Steg und schiebt dich rückwärts raus – gut dosieren, damit du nicht zu schnell auf die gegenüberliegenden Dalben treibst.',
    tags: ['Box', 'Ablegen', 'Wind'],
  },
  {
    id: 'ablegen-laengsseits-ablandig',
    title: 'Ablegen bei ablandigem Wind',
    difficulty: 2,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-a', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    env: { windSpeedKn: 10, windFromDeg: 0, gustiness: 0.2, windShiftDeg: 8 },
    briefing: 'Der Wind drückt dich vom Steg weg – das hilft. Alle Leinen zügig los, vertreiben lassen und dann voraus. Auf die Nachbarboote vorn und achtern achten.',
    tags: ['Längsseits', 'Ablegen', 'Wind'],
  },
  {
    id: 'ablegen-sued-box',
    title: 'Ablegen aus einer Süd-Box',
    difficulty: 2,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-s8', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 6, windFromDeg: 90, gustiness: 0.15, windShiftDeg: 8 },
    briefing: 'Rückwärts nach Norden in die Gasse. Der Radeffekt zieht das Heck nach Backbord – hier Richtung Westen, also Richtung Ausfahrt. Nutze ihn.',
    tags: ['Box', 'Ablegen', 'Radeffekt'],
  },
  {
    id: 'ablegen-box-strom',
    title: 'Ablegen mit leichter Strömung',
    difficulty: 2,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n16', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { currentSpeedKn: 0.4, currentTowardDeg: 90, windSpeedKn: 4, windFromDeg: 200 },
    briefing: 'Die Strömung setzt die Gasse entlang nach Osten. Beim Rausfahren versetzt sie dich – Richtung Ausfahrt musst du gegen den Strom.',
    tags: ['Box', 'Ablegen', 'Strömung'],
  },
  {
    id: 'ablegen-enge-box',
    title: 'Ablegen aus der engen Box am Eingang',
    difficulty: 3,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n3', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 8, windFromDeg: 250, gustiness: 0.25, windShiftDeg: 10 },
    briefing: 'Wenig Platz zu den Nachbarn und gleich die Ausfahrt. Gerade rückwärts raus, erst drehen wenn der Bug die Dalben passiert hat.',
    tags: ['Box', 'Ablegen', 'Präzision'],
  },
  {
    id: 'ablegen-laengsseits-stb',
    title: 'Ablegen Steuerbord längsseits',
    difficulty: 3,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-a', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'starboardSide',
    env: { windSpeedKn: 8, windFromDeg: 200, gustiness: 0.2, windShiftDeg: 8 },
    briefing: 'Du liegst mit Steuerbord am Steg, Bug nach Westen – Richtung Ausfahrt. Leicht auflandiger Wind: Heck zuerst abdrücken lassen (Eindampfen in die Vorspring), dann voraus.',
    tags: ['Längsseits', 'Ablegen', 'Eindampfen'],
  },
  {
    id: 'ablegen-heck-gegenwind',
    title: 'Vorwärts aus der Box gegen den Wind',
    difficulty: 3,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n10', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'sternToPier',
    env: { windSpeedKn: 12, windFromDeg: 180, gustiness: 0.25, windShiftDeg: 10 },
    briefing: 'Heck am Steg, der Wind bläst in die Box. Mit Druck vorwärts raus, Bugleinen an den Dalben erst im Vorbeifahren los – sonst drückt dich der Wind zurück an den Steg.',
    tags: ['Box', 'Ablegen', 'Wind'],
  },
  {
    id: 'ablegen-luecke-b',
    title: 'Ablegen aus Lücke B',
    difficulty: 3,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-b', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    env: { windSpeedKn: 8, windFromDeg: 220, gustiness: 0.2, windShiftDeg: 8 },
    briefing: 'Nur 14 m Lücke: vorn und achtern liegen Nachbarn dicht dran. Erst Abstand vom Steg gewinnen, dann gerade raus – Ballfender an die gefährdete Ecke.',
    tags: ['Längsseits', 'Ablegen', 'Präzision'],
  },
  {
    id: 'ablegen-achterspring',
    title: 'Ablegen: Eindampfen in die Achterspring',
    difficulty: 4,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-b', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    env: { windSpeedKn: 14, windFromDeg: 180, gustiness: 0.3, windShiftDeg: 8 },
    briefing:
      'Auflandig 14 kn, vorn kaum Platz. Alle Leinen außer der Achterspring los, Ballfender ans Heck, Ruder vom Steg weg und rückwärts eindampfen: der Bug schwenkt ab. Dann voraus raus.',
    tags: ['Längsseits', 'Ablegen', 'Eindampfen'],
  },
  {
    id: 'ablegen-langkieler-box',
    title: 'Langkieler aus der Box',
    difficulty: 4,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-n7', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 10, windFromDeg: 240, gustiness: 0.2, windShiftDeg: 8 },
    yacht: 'sy36-long',
    briefing: 'Langkieler rückwärts: kaum Steuerwirkung, kräftiger Radeffekt. Mit kurzen Gasstößen arbeiten und das Heck mit der Luv-Dalbenleine führen.',
    tags: ['Box', 'Ablegen', 'Langkiel'],
  },
  {
    id: 'ablegen-strom-laengsseits',
    title: 'Ablegen längsseits mit Strom von vorn',
    difficulty: 4,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-a', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    env: { currentSpeedKn: 1, currentTowardDeg: 270, windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15 },
    briefing: 'Die Strömung setzt mit 1 kn von vorn. Vorspring zuletzt los – die Strömung drückt dann den Bug vom Steg. Aber Achtung: Richtung Ausfahrt geht es mit dem Strom.',
    tags: ['Längsseits', 'Ablegen', 'Strömung'],
  },
  {
    id: 'ablegen-sturm-box',
    title: 'Sturmböen – raus aus Box Süd',
    difficulty: 5,
    harbor: 'boxengasse',
    goal: { kind: 'depart', berth: 'box-s13', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 25, windFromDeg: 240, gustiness: 0.5, windShiftDeg: 12, currentSpeedKn: 0.5, currentTowardDeg: 60 },
    briefing: '25 kn mit Böen und Strömung. Luv-Leinen zuletzt los, in einer Böenlücke zügig rückwärts raus und sofort Fahrt aufnehmen – sonst treibt es dich auf die Dalben.',
    tags: ['Box', 'Ablegen', 'Starkwind'],
  },
  {
    id: 'ablegen-langkieler-luecke-b',
    title: 'Langkieler aus Lücke B bei auflandigem Wind',
    difficulty: 5,
    harbor: 'laengsseits',
    goal: { kind: 'depart', berth: 'gap-b', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    env: { windSpeedKn: 16, windFromDeg: 200, gustiness: 0.4, windShiftDeg: 10, currentSpeedKn: 0.6, currentTowardDeg: 270 },
    yacht: 'sy36-long',
    briefing: 'Träger Langkieler, 14 m Lücke, auflandige Böen und Strömung. Eindampfen in eine Spring ist Pflicht – Fender und Ballfender an die Kontaktpunkte.',
    tags: ['Längsseits', 'Ablegen', 'Langkiel'],
  },
  // ================================================================ Einhand
  // Der Skipper ist allein: Er muss selbst an Deck, Gas und Ruder bleiben
  // solange stehen. Heckleinen sind nah am Cockpit, zum Bug ist es weit.
  {
    id: 'einhand-box-flaute',
    title: 'Einhand in die Box bei Flaute',
    difficulty: 2,
    harbor: 'boxengasse',
    crew: 'solo',
    goal: { kind: 'moor', berth: 'box-n10', orientation: 'bowToPier' },
    briefing:
      'Allein an Bord: Jeder Gang zum Bug dauert, und so lange steht das Ruder. Noch in der Gasse alle vier Leinen vorbereiten (J). Dann sehr langsam einlaufen, Heckleinen auf die Dalben, Maschine in Neutral – erst dann nach vorn zu den Bugleinen.',
    tags: ['Box', 'Einhand', 'Grundlagen'],
  },
  {
    id: 'einhand-laengsseits-flaute',
    title: 'Einhand längsseits bei Flaute',
    difficulty: 2,
    harbor: 'laengsseits',
    crew: 'solo',
    goal: { kind: 'moor', berth: 'gap-a', orientation: 'portSide' },
    briefing:
      'Fender und Leinen vorbereiten, solange du noch weit weg bist. Parallel an den Steg, zuerst eine Spring von der Mittelklampe (kurzer Weg) – dann kann das Boot nicht mehr weg, und du hast Zeit für Vor- und Achterleine.',
    tags: ['Längsseits', 'Einhand', 'Spring'],
  },
  {
    id: 'einhand-manoeverleinen-box',
    title: 'Einhand rückwärts in die Box mit Manöverleinen',
    difficulty: 3,
    harbor: 'boxengasse',
    crew: 'solo',
    goal: { kind: 'moor', berth: 'box-n12', orientation: 'sternToPier' },
    env: { windSpeedKn: 8, windFromDeg: 270, gustiness: 0.2, windShiftDeg: 8 },
    briefing:
      'Klassisches Einhand-Manöver: Unterwegs an beiden Achterklampen je eine Manöverleine vorbereiten (N, zweimal dieselbe Klampe) – doppelt über die Klampe, Holepart direkt im Cockpit. Rückwärts zwischen die Dalben, vom Cockpit aus über die Dalben werfen, dann mit den Reglern dosiert fieren: bremsen und gegen den Seitenwind lenken, ohne das Ruder zu verlassen. Am Steg festmachen, zum Schluss die Leinen zu den Dalben belegen.',
    tags: ['Box', 'Einhand', 'Manöverleine', 'Wind'],
  },
  {
    id: 'einhand-laengsseits-ablandig',
    title: 'Einhand längsseits, ablandiger Wind',
    difficulty: 4,
    harbor: 'laengsseits',
    crew: 'solo',
    goal: { kind: 'moor', berth: 'gap-b' },
    env: { windSpeedKn: 12, windFromDeg: 10, gustiness: 0.3, windShiftDeg: 10 },
    briefing:
      'Der Wind drückt vom Steg weg, und du bist allein. Mittelklampe als Spring auf einen Poller, belegen und leicht voraus eindampfen: Das Boot legt sich parallel an den Steg. Dann in Ruhe die übrigen Leinen.',
    tags: ['Längsseits', 'Einhand', 'Eindampfen', 'Wind'],
  },
  {
    id: 'einhand-box-seitenwind',
    title: 'Einhand in die Box bei starkem Seitenwind',
    difficulty: 5,
    harbor: 'boxengasse',
    crew: 'solo',
    goal: { kind: 'moor', berth: 'box-s8', orientation: 'bowToPier' },
    env: { windSpeedKn: 16, windFromDeg: 270, gustiness: 0.4, windShiftDeg: 12 },
    briefing:
      'Starker Seitenwind, niemand an Deck. Die Luv-Dalbe ist dein Halt: Manöverleine über die Luv-Dalbe ins Cockpit, dann einfahren und die Leine als Führung nutzen. Bugleinen erst, wenn das Boot sicher in der Box hängt.',
    tags: ['Box', 'Einhand', 'Manöverleine', 'Starkwind'],
  },
  {
    id: 'einhand-ablegen-box',
    title: 'Einhand rückwärts aus der Box',
    difficulty: 2,
    harbor: 'boxengasse',
    crew: 'solo',
    goal: { kind: 'depart', berth: 'box-n10', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 5, windFromDeg: 200, gustiness: 0.1, windShiftDeg: 5 },
    briefing:
      'Vorher die Heckleinen auf Slip legen: Manöverleinen über die Dalben ins Cockpit führen, festen Leinen los. Bugleinen los, zurück ans Ruder, rückwärts raus und die Manöverleinen vom Cockpit über Slip einholen.',
    tags: ['Box', 'Einhand', 'Ablegen', 'Manöverleine'],
  },
  {
    id: 'einhand-ablegen-laengsseits',
    title: 'Einhand ablegen längsseits',
    difficulty: 2,
    harbor: 'laengsseits',
    crew: 'solo',
    goal: { kind: 'depart', berth: 'gap-a', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    briefing:
      'Alles bis auf eine Spring lösen, bevor du ans Ruder gehst. Die letzte Leine als Manöverleine auf Slip legen und ins Cockpit führen – dann kannst du sie vom Ruder aus einholen.',
    tags: ['Längsseits', 'Einhand', 'Ablegen', 'Manöverleine'],
  },
  {
    id: 'einhand-ablegen-box-wind',
    title: 'Einhand vorwärts aus der Box mit Seitenwind',
    difficulty: 3,
    harbor: 'boxengasse',
    crew: 'solo',
    goal: { kind: 'depart', berth: 'box-n12', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'sternToPier',
    env: { windSpeedKn: 10, windFromDeg: 270, gustiness: 0.25, windShiftDeg: 8 },
    briefing:
      'Heck am Steg, Wind von der Seite. Die Luv-Leine an der Dalbe auf Slip ins Cockpit führen, alle anderen los. Voraus raus und die Slip-Leine erst einholen, wenn der Bug frei ist.',
    tags: ['Box', 'Einhand', 'Ablegen', 'Wind'],
  },
  {
    id: 'einhand-ablegen-auflandig',
    title: 'Einhand ablegen bei auflandigem Wind',
    difficulty: 4,
    harbor: 'laengsseits',
    crew: 'solo',
    goal: { kind: 'depart', berth: 'gap-a', zone: EXIT_LAENGSSEITS, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'portSide',
    env: { windSpeedKn: 12, windFromDeg: 200, gustiness: 0.3, windShiftDeg: 8 },
    briefing:
      'Der Wind drückt an den Steg. Vorspring als Manöverleine ins Cockpit, alle anderen Leinen los, Fender am Bug. Rückwärts in die Spring eindampfen, bis das Heck frei ist, dann Slip einholen und zügig rückwärts ab.',
    tags: ['Längsseits', 'Einhand', 'Ablegen', 'Eindampfen'],
  },
  {
    id: 'einhand-ablegen-sturm',
    title: 'Einhand raus aus der Box bei Starkwind',
    difficulty: 5,
    harbor: 'boxengasse',
    crew: 'solo',
    goal: { kind: 'depart', berth: 'box-n7', zone: EXIT_BOXENGASSE, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 18, windFromDeg: 240, gustiness: 0.4, windShiftDeg: 12 },
    briefing:
      'Starker, böiger Wind schräg von achtern. Beide Heckleinen als Manöverleinen ins Cockpit, Bugleinen los und sofort zurück ans Ruder. Rückwärts raus und mit dem Regler die Luv-Leine länger halten.',
    tags: ['Box', 'Einhand', 'Ablegen', 'Starkwind'],
  },
  // ======================================================= Enge Gassen
  {
    id: 'eng-box-vorwaerts',
    title: 'Enge Gasse: vorwärts in die Box',
    difficulty: 3,
    harbor: 'enge-gasse',
    goal: { kind: 'moor', berth: 'box-n9', orientation: 'bowToPier' },
    env: { windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15, windShiftDeg: 6 },
    briefing:
      'Nur 15 m Fahrwasser und schmale Boxen. Weit außen an der gegenüberliegenden Dalbenreihe halten, früh einlenken – und den Radeffekt beim Aufstoppen einplanen, sonst steht das Heck am Nachbarn.',
    tags: ['Box', 'Enge Gasse'],
  },
  {
    id: 'eng-rueckwaerts-seitenwind',
    title: 'Enge Gasse: rückwärts bei Seitenwind',
    difficulty: 4,
    harbor: 'enge-gasse',
    goal: { kind: 'moor', berth: 'box-s6', orientation: 'sternToPier' },
    env: { windSpeedKn: 12, windFromDeg: 270, gustiness: 0.3, windShiftDeg: 10 },
    briefing:
      'Der Wind kommt von der Einfahrt. In der engen Gasse vorher drehen ist kaum möglich: Schon vor der Gasse wenden und die ganze Strecke rückwärts fahren. Bug in den Wind halten, Luv-Dalbe zuerst belegen.',
    tags: ['Box', 'Enge Gasse', 'Rückwärts', 'Wind'],
  },
  {
    id: 'eng-ablegen',
    title: 'Enge Gasse: rückwärts raus und drehen',
    difficulty: 3,
    harbor: 'enge-gasse',
    goal: { kind: 'depart', berth: 'box-n9', zone: EXIT_ENGE, zoneLabel: 'Gasseneinfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15, windShiftDeg: 6 },
    briefing:
      'Rückwärts aus der Box und in 15 m Fahrwasser auf der Stelle drehen: Radeffekt nutzen – rückwärts zieht das Heck nach Backbord, dann voraus mit Ruder hart und kurzen Gasstößen.',
    tags: ['Box', 'Enge Gasse', 'Ablegen', 'Radeffekt'],
  },
  {
    id: 'eng-ablegen-boeig',
    title: 'Enge Gasse: Ablegen in Böen',
    difficulty: 4,
    harbor: 'enge-gasse',
    goal: { kind: 'depart', berth: 'box-s12', zone: EXIT_ENGE, zoneLabel: 'Gasseneinfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 14, windFromDeg: 240, gustiness: 0.4, windShiftDeg: 12 },
    briefing:
      'Böiger Wind schräg in die Gasse, kaum Platz zum Drehen. Überlege, ob du in der Gasse drehst oder die ganze Strecke rückwärts zur Einfahrt fährst – der Bug fällt in Böen schnell ab.',
    tags: ['Box', 'Enge Gasse', 'Ablegen', 'Wind'],
  },
  // =================================================== Hafen mit Seitengassen
  {
    id: 'seiten-g1-vorwaerts',
    title: 'Seitengasse 1: vorwärts in die Box',
    difficulty: 3,
    harbor: 'seitengassen',
    goal: { kind: 'moor', berth: 'box-g1-n3', orientation: 'bowToPier' },
    env: { windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15, windShiftDeg: 6 },
    briefing:
      'Vom Hauptfahrwasser nach links in Gasse 1 abbiegen (16 m breit), dann nach links in die Box. Weit ausholen: Die Kurve in die Gasse und die Kurve in die Box folgen dicht aufeinander.',
    tags: ['Box', 'Seitengasse'],
  },
  {
    id: 'seiten-g2-rueckwaerts',
    title: 'Seitengasse 2: rückwärts hinein',
    difficulty: 4,
    harbor: 'seitengassen',
    goal: { kind: 'moor', berth: 'box-g2-n5', orientation: 'sternToPier' },
    env: { windSpeedKn: 8, windFromDeg: 180, gustiness: 0.2, windShiftDeg: 8 },
    briefing:
      'Gasse 2 ist nur 12 m breit, die Box liegt tief drin – drehen ist dort praktisch unmöglich. Also schon im Hauptfahrwasser wenden und die ganze Gasse rückwärts hineinfahren. Der Radeffekt zieht das Heck nach Backbord: vorher etwas nach Steuerbord versetzt ansetzen.',
    tags: ['Box', 'Seitengasse', 'Rückwärts', 'Entscheidung'],
  },
  {
    id: 'seiten-g2-wahl',
    title: 'Seitengasse 2: du entscheidest',
    difficulty: 5,
    harbor: 'seitengassen',
    goal: { kind: 'moor', berth: 'box-g2-s4' },
    env: { windSpeedKn: 15, windFromDeg: 270, gustiness: 0.45, windShiftDeg: 12 },
    briefing:
      'Böiger Wind von West, Gasse 2 ist eng und endet an einer Mauer. Vorwärts oder rückwärts in die Box – entscheide vor der Abzweigung, in der Gasse gibt es kaum ein Zurück. Tipp: Vorwärts hinein heißt später rückwärts die ganze Gasse hinaus.',
    tags: ['Box', 'Seitengasse', 'Entscheidung', 'Starkwind'],
  },
  {
    id: 'seiten-einhand-g1',
    title: 'Einhand rückwärts in Seitengasse 1',
    difficulty: 5,
    harbor: 'seitengassen',
    crew: 'solo',
    goal: { kind: 'moor', berth: 'box-g1-n6', orientation: 'sternToPier' },
    env: { windSpeedKn: 10, windFromDeg: 200, gustiness: 0.3, windShiftDeg: 8 },
    briefing:
      'Allein, rückwärts, ganz hinten in der Gasse. Im Hauptfahrwasser beide Achterklampen als Manöverleinen vorbereiten (doppelt über die Klampe ins Cockpit), dann rückwärts in die Gasse, über die Dalben werfen und mit den Reglern bremsen und lenken.',
    tags: ['Box', 'Seitengasse', 'Einhand', 'Manöverleine'],
  },
  {
    id: 'seiten-ablegen-g1',
    title: 'Aus Seitengasse 1 ablegen',
    difficulty: 3,
    harbor: 'seitengassen',
    goal: { kind: 'depart', berth: 'box-g1-s2', zone: EXIT_SEITEN, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 6, windFromDeg: 200, gustiness: 0.15, windShiftDeg: 6 },
    briefing:
      'Rückwärts aus der Box, in der 16 m breiten Gasse drehen oder rückwärts bis ins Hauptfahrwasser – beides geht. Dann nach Westen zur Ausfahrt.',
    tags: ['Box', 'Seitengasse', 'Ablegen'],
  },
  {
    id: 'seiten-ablegen-g2-heck',
    title: 'Aus Seitengasse 2 vorwärts raus',
    difficulty: 3,
    harbor: 'seitengassen',
    goal: { kind: 'depart', berth: 'box-g2-n5', zone: EXIT_SEITEN, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'sternToPier',
    env: { windSpeedKn: 8, windFromDeg: 200, gustiness: 0.2, windShiftDeg: 8 },
    briefing:
      'Wer rückwärts hineingefahren ist, kommt bequem vorwärts heraus: Dalbenleinen los, voraus aus der Box und gleich in Richtung Mündung einlenken – in der 12 m breiten Gasse ist für eine weite Kurve kein Platz.',
    tags: ['Box', 'Seitengasse', 'Ablegen'],
  },
  {
    id: 'seiten-ablegen-g2-bug',
    title: 'Aus Seitengasse 2 rückwärts hinaus',
    difficulty: 4,
    harbor: 'seitengassen',
    goal: { kind: 'depart', berth: 'box-g2-n2', zone: EXIT_SEITEN, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 8, windFromDeg: 240, gustiness: 0.2, windShiftDeg: 8 },
    briefing:
      'Bug zum Steg in der engen Gasse: Drehen geht dort praktisch nicht, also rückwärts aus der Box, das Heck Richtung Mündung schwenken und rückwärts bis ins Hauptfahrwasser. Erst dort drehen.',
    tags: ['Box', 'Seitengasse', 'Ablegen', 'Rückwärts'],
  },
  {
    id: 'seiten-ablegen-einhand',
    title: 'Einhand aus Seitengasse 2 bei Böen',
    difficulty: 5,
    harbor: 'seitengassen',
    crew: 'solo',
    goal: { kind: 'depart', berth: 'box-g2-s4', zone: EXIT_SEITEN, zoneLabel: 'Hafenausfahrt (West)' },
    startMoored: 'bowToPier',
    env: { windSpeedKn: 12, windFromDeg: 240, gustiness: 0.35, windShiftDeg: 10 },
    briefing:
      'Allein in der engen Gasse, Böen von achtern. Heckleinen auf Slip ins Cockpit legen, Bugleinen los, zurück ans Ruder. Rückwärts raus, die Slip-Leinen erst einholen, wenn das Boot sauber in der Gasse liegt – dann rückwärts bis ins Hauptfahrwasser.',
    tags: ['Box', 'Seitengasse', 'Einhand', 'Ablegen'],
  },
];

export function findTask(id: string | null | undefined): TaskDef | undefined {
  return TASKS.find((t) => t.id === id);
}

/** Aufgaben sortiert nach Schwierigkeit (stabile Reihenfolge innerhalb). */
export function tasksByDifficulty(): TaskDef[] {
  return TASKS.map((t, i) => ({ t, i }))
    .sort((a, b) => a.t.difficulty - b.t.difficulty || a.i - b.i)
    .map((x) => x.t);
}

/** Nächste Aufgabe in Lernreihenfolge (oder undefined am Ende). */
export function nextTask(id: string): TaskDef | undefined {
  const list = tasksByDifficulty();
  const i = list.findIndex((t) => t.id === id);
  return i >= 0 ? list[i + 1] : undefined;
}
